/* global */
// Centralized data for ABC Ongedierte

// Single source of truth voor review-counts.
// Fallback-waarden voor de prerendered HTML (zodat Google bij het indexeren
// concrete getallen ziet). Bij client-side load wordt dit upgegrade naar de
// echte live cijfers via getGoogleAggregate() (Cloudflare Worker → Google
// Places API). Update deze fallback handmatig bij een grote sprong, of
// laat 'm gewoon achterlopen — de gebruiker ziet altijd de live versie.
export const REVIEWS = {
  total: 159,
  rating: 4.6,
  ratingStr: "4,6",        // NL-notatie (komma) — gebruik in copy + UI
  ratingDisplay: "4,6",    // alias voor consistentie met audit-brief
  source: "Google",
};

// Centrale business-stats. Single source of truth voor cijfers die
// op meerdere plekken in copy/UI/schema voorkomen. Bij wijziging:
// alleen hier aanpassen, alles propageert automatisch via imports.
export const BUSINESS_STATS = {
  // Aantal gemeenten in werkgebied — afgeleid van STEDEN.length voor
  // automatische sync. Bij toevoegen/verwijderen van steden klopt de
  // teller direct. Tijdens prerender = build-time waarde.
  get municipalitiesServed() { return STEDEN.length; },
  // Totaal aantal tevreden klanten sinds oprichting (2017). Gebruikt
  // door TrustLuxe in hero ("10.000+ klanten in de Randstad").
  // Cumulatief — gerond naar onder voor conservatieve copy.
  customersAllTime: 10000,
  // Klanten per jaar (gemiddeld). Gebruikt door OverOnsPage in de
  // statistieken-strip. Andere metric dan customersAllTime.
  customersPerYear: 1500,
  // Jaar van oprichting (KvK 71298592).
  foundedYear: 2017,
  // Berekend at-call: actuele jaren actief.
  get yearsActive() { return new Date().getFullYear() - this.foundedYear; },
};

// Centrale openingstijden — header + footer + contact + JSON-LD lezen
// allen uit deze constante zodat ze nooit uit sync raken.
export const OPENING_HOURS = {
  // Voor copy in nav/footer/contact.
  weekdayLabel: "Ma-Vr 07:00-22:00",
  weekendLabel: "Za-Zo 09:00-20:00",
  emergencyLabel: "Spoed 24/7",
  // Voor JSON-LD openingHoursSpecification (Schema.org formaat).
  schema: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "22:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday", "Sunday"],
      opens: "09:00",
      closes: "20:00",
    },
  ],
};

// Indexable stad×plaag combinaties — alleen deze 15 grote bevolkings-
// centra mogen door Google geïndexeerd worden (strategie A uit SEO-audit).
// De overige 102 steden krijgen <meta robots="noindex,follow"> en worden
// uit de sitemap weggelaten zodat ze niet meer scoren voor duplicate
// content, terwijl interne links blijven werken voor UX.
export const INDEXABLE_CITIES = new Set([
  "rotterdam", "den-haag", "amsterdam", "utrecht",
  "leiden", "delft", "dordrecht", "haarlem",
  "zoetermeer", "amersfoort", "alphen", "gouda",
  "spijkenisse", "schiedam", "vlaardingen",
]);

// Google Business Profile "schrijf een review"-link — wordt gebruikt door
// de rating-bar zodat bezoekers direct op de Google reviews-pagina van het
// bedrijf landen (lezen + zelf review achterlaten).
export const GOOGLE_REVIEWS_URL = "https://share.google/3aCLkVivOTmJL1MjV";

// ─── Form-routing ──────────────────────────────────────────────────────
// Alle formulieren op de site worden verzonden via Web3Forms
// (https://web3forms.com — gratis, geen account nodig, infinite submissions).
// De Worker neemt het POST'je aan en stuurt het door als email naar
// `FORM_EMAIL`. Eén key voor alle forms; één regel om straks te switchen
// van Gmail-testaccount naar info@abcongedierte.nl productie.
//
// SETUP-stappen (eenmalig):
//   1. https://web3forms.com → "Get Access Key" → vul FORM_EMAIL in
//   2. Check inbox voor verificatie-mail → klik link
//   3. Plak access-key hieronder
//   4. Rebuild + deploy
//
// SECURITY: deze key is public — mag in frontend bundle. Hij heeft alleen
// "send to FORM_EMAIL" rechten, niet meer.
export const FORM_EMAIL = "info@abcongedierte.nl";
export const WEB3FORMS_KEY = "1cfc6723-202c-411b-a68d-4cb9776afe33";

export const PESTS = [
  {
    slug: "muizen",
    nl: "Muizen",
    lat: "Mus musculus",
    from: "",
    short: "Muizen verspreiden ziektes en veroorzaken schade. Wij verwijderen ze snel en voorkomen terugkeer.",
    intro: "Hoort u 's nachts geritsel boven het plafond, of vindt u kleine donkere keuteltjes in een keukenla? Grote kans dat u te maken heeft met huismuizen. Wij sporen op, sluiten af en bestrijden vakkundig.",
    signs: [
      "Donkere keuteltjes van ongeveer 6 mm",
      "Knaagsporen op voedselverpakkingen",
      "Geritsel achter wanden of boven plafond, vooral 's nachts",
      "Muffe, urine-achtige geur",
      "Vetafdrukken langs vaste looproutes",
    ],
    method: [
      "Inspectie van keuken, kelder, zolder en kruipruimte",
      "Lokale slag- en lokdoosjes op strategische plekken",
      "Kleine openingen en kieren dichten (muizen passen door 6 mm)",
      "Monitoring na 2 weken om resultaat te controleren",
      "Adviezen om herhaling te voorkomen",
    ],
    prevent: [
      "Bewaar etenswaren in afgesloten bakjes",
      "Geen open afval in keuken laten staan",
      "Controleer 2× per jaar gevel op kieren en kabelopeningen",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 7-14 dagen",
  },
  {
    slug: "ratten",
    nl: "Ratten",
    lat: "Rattus norvegicus",
    from: "",
    short: "Ratten kunnen ziektes overbrengen en flinke schade aanrichten. Onze specialisten pakken ze snel en grondig aan.",
    intro: "Een rat in huis of tuin is geen kleinigheid: ze knagen aan kabels, verspreiden ziektes en planten zich snel voort. Bovendien is er meldplicht. Bel ons direct, we komen vandaag indien nodig.",
    signs: [
      "Grote, donkere keuteltjes van 15-20 mm",
      "Knaagschade aan kabels, leidingen of houtwerk",
      "Loopsporen langs muren of in vochtig zand",
      "Sterke ammoniakgeur in kruipruimte of schuur",
      "Gaten van 5+ cm rond kelder of fundering",
    ],
    method: [
      "Inspectie van huis, kruipruimte, tuin en aangrenzende panden",
      "Externe bestrijding bij riool- of tuin-overlast",
      "Lokboxen binnenshuis indien nodig, altijd met afsluitbare lokstations",
      "Controle na 7 dagen, indien nodig vervolgbehandeling",
      "Riool-camera op aanvraag bij verdenking op aansluiting",
    ],
    prevent: [
      "Composthoop minimaal 5 meter van het huis",
      "Geen voederbakken huisdieren 's nachts buiten laten",
      "Controleer kruipruimte 2× per jaar op gaten en geur",
    ],
    visits: "2-3 bezoeken",
    duration: "Resultaat binnen 2-4 weken",
  },
  {
    slug: "wespen",
    nl: "Wespen",
    lat: "Vespula vulgaris",
    from: "",
    short: "Wespennesten zijn gevaarlijk. Wij verwijderen het nest veilig, ook op moeilijk bereikbare plekken.",
    intro: "Wespen worden agressief in augustus en september, zeker als hun nest dichtbij is. Wij verwijderen het nest veilig, in een dakgoot, spouwmuur, schuur of zelfs onder de grond.",
    signs: [
      "Constante aanvliegroute naar één punt",
      "Papier-achtig grijs nest zichtbaar onder afdak",
      "Agressieve wespen rond hetzelfde tijdstip",
      "Brommend geluid vanuit een spouw of plafond",
    ],
    method: [
      "Veilige benadering met beschermende kleding",
      "Nest verwijderen of behandelen met poeder",
      "Twee-traps aanpak voor terugkerende werksters",
      "Hoogwerker bij hoge nesten (op offerte)",
    ],
    prevent: [
      "Controleer dakgoten in voorjaar op begin van een nest",
      "Houd zoete dranken afgedekt in tuin",
      "Plaats roosters voor ventilatieopeningen",
    ],
    visits: "1 bezoek (meestal)",
    duration: "Effect direct, restpopulatie verdwijnt in 7 dagen",
  },
  {
    slug: "bedwantsen",
    nl: "Bedwantsen",
    lat: "Cimex lectularius",
    from: "",
    short: "Bedwantsen zijn hardnekkig. Onze behandeling elimineert ze compleet, inclusief eieren en larven.",
    intro: "Bedwantsen zijn hardnekkige overlevers. Sproeien werkt nauwelijks; wij gebruiken hitte (60°C) of CO₂-frost om alle stadia in één keer te elimineren. Discreet en zonder dat u uw matras of meubels hoeft weg te gooien.",
    signs: [
      "Rijen kleine, jeukende beten, vaak op armen of rug",
      "Bloedvlekjes op het laken",
      "Donkere stipjes (uitwerpselen) op matrasrand",
      "Zoetige, muffe geur in slaapkamer",
    ],
    method: [
      "Volledige inspectie van slaapkamer, woonkamer en wasgoed",
      "Hitte-behandeling op 60°C (90 min) of CO₂-frost",
      "Geen sproeien, werkt nauwelijks en spreidt de wantsen",
      "Controle na 14 dagen om uitkomen van eieren af te dekken",
    ],
    prevent: [
      "Bij thuiskomst van reis: koffer direct uitpakken in garage",
      "Controleer 2× per jaar matrasrand en plinthoeken",
      "Tweedehands matrassen alleen na controle aannemen",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat na 2 weken",
  },
  {
    slug: "mieren",
    nl: "Mieren",
    lat: "Lasius niger",
    from: "",
    short: "Een mierenkolonie groeit razendsnel. Wij vinden de koningin en elimineren de volledige kolonie.",
    intro: "Een mierenstraat lijkt onschuldig, maar wijst op een kolonie van duizenden onder uw tegels of in de spouwmuur. Wij werken niet met contact-spray (dat splitst het nest), maar met gel-aas dat naar de koningin gaat.",
    signs: [
      "Loopstraat door de keuken of badkamer",
      "Zandhoopjes tussen tegels of in voegen",
      "Vleugel-mieren binnenshuis in juni (bruidsvlucht)",
      "Mieren rond etensresten of huisdiervoer",
    ],
    method: [
      "Identificatie van soort (zwart, geel, bos)",
      "Gel-aas op looproute, werksters brengen het naar het nest",
      "Geen contact-spray, want die splitst de kolonie",
      "Resultaat binnen 7 dagen, kolonie volledig dood",
    ],
    prevent: [
      "Maak kruimels en suiker direct op",
      "Sluit huisdier-voederbakken af buiten etenstijd",
      "Vul kieren rond plinten en kozijnen",
    ],
    visits: "1 bezoek",
    duration: "Resultaat binnen 7 dagen",
  },
  {
    slug: "kakkerlakken",
    nl: "Kakkerlakken",
    lat: "Blattella germanica",
    from: "",
    short: "Kakkerlakken zijn een gezondheidsgevaar en heel moeilijk zelf te bestrijden. Wij pakken ze grondig aan.",
    intro: "Kakkerlakken zijn nachtactief en wijzen meestal op een groter probleem in het pand of de aangrenzende panden. Voor horeca een HACCP-risico. Wij doen een grondige aanpak in drie bezoeken met monitor-strips en plattegrond.",
    signs: [
      "'s Nachts beestjes wegrennen bij licht aandoen",
      "Donkere uitwerpsel-stipjes langs plinten",
      "Muffe, zoetige geur, vooral in keukens",
      "Lege eipakketjes (ootheca) in hoeken",
    ],
    method: [
      "Inspectie van keuken, opslag en leidingschachten",
      "Gel-baiting in scheuren en kieren",
      "Monitor-strips om activiteit te meten",
      "Plattegrond met alle haarden en behandelpunten",
      "Drie bezoeken voor volledige eliminatie",
    ],
    prevent: [
      "Sluit afvoeren met fijne roosters",
      "Vermijd kartonnen dozen langdurig op vloer (schuilplek)",
      "Houd keuken volledig droog na sluitingstijd",
    ],
    visits: "3 bezoeken",
    duration: "Resultaat in 4-6 weken",
  },
  {
    slug: "spinnen",
    nl: "Spinnen",
    lat: "Araneae",
    from: "",
    comingSoon: true,
    short: "Veel spinnen in huis of bedrijf? Wij verwijderen webben en behandelen kozijnen, plinten en geveldelen.",
    intro: "De meeste spinnen in Nederland zijn ongevaarlijk, maar in grote aantallen geven ze overlast, vooral in het najaar. Wij verwijderen webben, behandelen kruipruimte, kozijnen en geveldelen, en geven advies om de aantrekkingskracht weg te nemen.",
    signs: [
      "Webben in hoeken van plafonds, kozijnen of zolders",
      "Spinnen 's avonds op buitenmuren rond lampen",
      "Veel kleine spinnen in kruipruimte of garage",
      "Eierzakjes (witte balletjes) onder vensterbanken",
    ],
    method: [
      "Inspectie binnen + gevelinspectie buiten",
      "Verwijderen van webben en eierzakjes",
      "Residuele behandeling van kozijnen, plinten en geveldelen",
      "Advies over verlichting en vlieg-overlast (voedselbron)",
    ],
    prevent: [
      "Houd buitenverlichting op gele/warmwitte LED, minder insecten",
      "Sluit ventilatieroosters met fijne gaas",
      "Stof regelmatig hoeken en plafondranden",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 1-2 weken",
  },
  {
    slug: "rioolvliegjes",
    nl: "Rioolvliegjes",
    lat: "Psychodidae",
    from: "",
    comingSoon: true,
    short: "Kleine, donzige vliegjes uit doucheputje of gootsteen. Wij sporen de broedplaats op en behandelen de afvoer.",
    intro: "Rioolvliegjes (motmuggen) komen uit de slijmlaag in afvoerleidingen. Ze zijn niet schadelijk, maar wijzen op een verstopping of biofilm. Wij behandelen de afvoer biologisch en geven advies om herhaling te voorkomen.",
    signs: [
      "Kleine, donzig-grijze vliegjes rond doucheputje",
      "Stilzittende vliegjes op tegels in badkamer",
      "Toename na douchen of warm water",
      "Slecht aflopende afvoer of muffe geur",
    ],
    method: [
      "Identificatie van getroffen afvoeren (douche, gootsteen, toilet)",
      "Biologische enzymen om biofilm af te breken",
      "Mechanische reiniging waar nodig",
      "Controle na 2 weken, broedcyclus is 10-14 dagen",
    ],
    prevent: [
      "Spoel afvoeren wekelijks met heet water",
      "Maandelijks enzymen-onderhoudsmiddel gebruiken",
      "Haar en zeepresten uit doucheputje halen",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 2-3 weken",
  },
  {
    slug: "vliegen",
    nl: "Vliegen",
    lat: "Musca domestica",
    from: "",
    comingSoon: true,
    short: "Vliegen verspreiden bacteriën en zijn een HACCP-risico. Wij sporen de broedplaats op en bestrijden gericht.",
    intro: "Een paar vliegen in de zomer is normaal, tientallen of honderden wijzen op een broedplaats binnen 100 meter. Voor horeca een HACCP-risico. Wij sporen op, behandelen en plaatsen UV-vangstunits waar nodig.",
    signs: [
      "Vele vliegen rond ramen of lichtbronnen",
      "Maden of pupariën in afvalbak of compost",
      "Plotse toename na warme dagen",
      "Vliegen rond afvalcontainers of dierenverblijven",
    ],
    method: [
      "Broedplaats opsporen (afval, kadaver, organisch materiaal)",
      "Bron verwijderen of behandelen",
      "Adulticide spray of UV-vangstunit (zakelijk)",
      "Advies over hygiëne en afvalbeheer",
    ],
    prevent: [
      "Gesloten afvalcontainers, dagelijks legen in zomer",
      "Hor voor ramen en deuren in keukens",
      "Geen voedselresten of dierenuitwerpselen laten liggen",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 1-2 weken",
  },
  {
    slug: "vlooien",
    nl: "Vlooien",
    lat: "Ctenocephalides felis",
    from: "",
    comingSoon: true,
    short: "Vlooien komen meestal mee met huisdieren. Wij behandelen huis, tapijten en stoffering, eieren inclusief.",
    intro: "Vlooien komen vrijwel altijd mee met hond of kat, maar ook na een leegstaande woning kunnen ze actief worden. Eieren overleven in tapijt en plinten. Wij behandelen het hele oppervlak, eieren en larven inbegrepen.",
    signs: [
      "Jeukende beten rond enkels, vaak in rijtjes",
      "Springende, donkerbruine beestjes op huisdier of vloer",
      "Zwart-bruine korreltjes (vlooien-poep) in vacht of mand",
      "Plotse activiteit na vakantie of in een nieuw huis",
    ],
    method: [
      "Inspectie van huis, vooral slaap- en zitplekken van huisdier",
      "Stofzuigen vooraf (activeert larven uit poppenstadium)",
      "Behandeling van tapijten, plinten en stoffering met IGR",
      "Behandeling huisdier door dierenarts blijft noodzakelijk",
    ],
    prevent: [
      "Vlooien-preventie huisdier (pipet of band) jaarrond",
      "Wekelijks stofzuigen, vooral in zomer",
      "Huisdieren-mand maandelijks op 60°C wassen",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 3-4 weken",
  },
  {
    slug: "zilvervisjes",
    nl: "Zilvervisjes",
    lat: "Lepisma saccharinum",
    from: "",
    comingSoon: true,
    short: "Zilvervisjes wijzen op vocht. Wij behandelen schuilplekken en geven advies om de vochtoorzaak weg te nemen.",
    intro: "Zilvervisjes zijn nachtactief en houden van vocht. Ze beschadigen boeken, papier en textiel. Ze zijn niet gevaarlijk, maar wijzen vrijwel altijd op een te hoge luchtvochtigheid in badkamer, keuken of kelder.",
    signs: [
      "Zilver-grijze beestjes (8-12 mm) in badkamer of keuken",
      "Snelle, slingerende loopbeweging bij licht aandoen",
      "Schade aan oude boeken, behang of foto's",
      "Vaak in combinatie met schimmelplekken",
    ],
    method: [
      "Inspectie op vochtbronnen en schuilplekken",
      "Lokaal aas in scheuren en kieren",
      "Behandeling plinten, badkamerkasten en kelder",
      "Advies over ontvochtigen (luchten, mechanische ventilatie)",
    ],
    prevent: [
      "Luchtvochtigheid onder 55%, ventileer badkamer",
      "Sluit kieren rond leidingen en plinten",
      "Bewaar boeken/papier in droge ruimtes",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 2-4 weken",
  },
  {
    slug: "papiervisjes",
    nl: "Papiervisjes",
    lat: "Ctenolepisma longicaudata",
    from: "",
    comingSoon: true,
    short: "Papiervisjes zijn hardnekkiger dan zilvervisjes. Wij doen meerdere behandelingen voor volledige eliminatie.",
    intro: "Papiervisjes lijken op zilvervisjes maar zijn groter (tot 19 mm) en veel hardnekkiger, ze overleven ook in droge ruimtes. Ze veroorzaken schade aan boeken, archief en behang. Eén behandeling is meestal niet genoeg.",
    signs: [
      "Grotere zilver-grijze beestjes (tot 19 mm) door het hele huis",
      "Schade aan boeken, archieven, behang en isolatiemateriaal",
      "Activiteit niet beperkt tot badkamer, ook in slaapkamer/kantoor",
      "Worden 's nachts vaker waargenomen",
    ],
    method: [
      "Volledige inspectie, papiervisjes verspreiden zich door het hele pand",
      "Gel-aas met IGR in scheuren en achter plinten",
      "Behandeling kruipruimte, zolder en spouwwanden indien nodig",
      "Twee tot drie behandelingen met 4-6 weken tussenpoos",
    ],
    prevent: [
      "Sluit kieren in plinten, kozijnen en rond leidingen",
      "Beperk papier-opslag in vochtige kelders",
      "Houd luchtvochtigheid onder 55%",
    ],
    visits: "2-3 bezoeken",
    duration: "Resultaat binnen 8-12 weken",
  },
  {
    slug: "motten",
    nl: "Motten",
    lat: "Tineola bisselliella / Plodia interpunctella",
    from: "",
    comingSoon: true,
    short: "Kleermotten in kast of voedselmotten in voorraad? Wij bestrijden volwassen motten, larven én eitjes.",
    intro: "Twee soorten motten geven binnen overlast: kleermotten (kasten, wollen kleding, tapijt) en voedselmotten (meel, rijst, noten, hondenvoer). Beide hebben dezelfde aanpak: bron weghalen, feromoonvallen plaatsen en larven + eitjes elimineren met een residuele behandeling.",
    signs: [
      "Kleine, vliegende beige-bruine vlinders in kasten of keuken",
      "Gaatjes in wollen kleding, vilt of tapijt (kleermotten)",
      "Web-achtige draadjes in meel-, rijst- of notenverpakkingen (voedselmotten)",
      "Verpopte cocons in hoeken van kasten of plafonds",
      "Larven (kleine witte rupsjes) tussen voedsel of in textiel",
    ],
    method: [
      "Identificatie soort (kleer- of voedselmot), verschillende aanpak",
      "Bronopsporing: alle besmette kasten, voorraden en textiel",
      "Vernietigen / wassen besmet materiaal op 60°C",
      "Feromoonvallen om mannetjes te vangen + voortplanting te stoppen",
      "Residuele behandeling van kastranden, plinten en hoeken",
    ],
    prevent: [
      "Bewaar voedsel in luchtdichte glazen of plastic containers",
      "Hang wollen kleding in zomer in cederhout-zakken of bewaar diepvries 48u",
      "Maak kasten 2× per jaar leeg en stof grondig",
      "Plaats feromoonvallen permanent in voorraadkast als monitoring",
    ],
    visits: "1-2 bezoeken",
    duration: "Resultaat binnen 3-6 weken (motten-levenscyclus)",
  },
  {
    slug: "mollen",
    nl: "Mollen",
    lat: "Talpa europaea",
    from: "",
    comingSoon: true,
    short: "Molshopen in uw gazon? Wij vangen mollen vakkundig met mechanische vangkasten, gegarandeerd effectief.",
    intro: "Mollen vreten je gazon niet, maar hun gangenstelsels en bulthopen verwoesten wel het zicht én de wortels van het gras. Eén mol kan binnen weken tientallen molshopen veroorzaken. Wij zetten professionele klemtypevangers in die voldoen aan de wettelijke vangstcriteria, humane én effectieve aanpak.",
    signs: [
      "Verse bulthopen (molshopen) in gazon of border",
      "Zachte gronddraad langs onzichtbare ondergrondse gangen",
      "Verzakkingen en kale plekken in gras boven gangen",
      "Schade aan moestuin-wortels of bloembollen",
      "Sterk toenemend aantal hopen in lente of najaar",
    ],
    method: [
      "Inspectie van het terrein: actieve gangen vs lege gangen onderscheiden",
      "Plaatsing van 2-4 mechanische vangkasten in hoofd-doorgangen",
      "Controle om de 2-3 dagen, herplaatsen bij geen vangst",
      "Egaliseren molshopen na succesvol vangen",
      "Vervolg-monitoring 14 dagen tegen herkolonisatie",
    ],
    prevent: [
      "Houd het gazon kort, mollen mijden open plekken",
      "Reduceer regenwormen-populatie (mollen-hoofdvoedsel) door minder bemesten",
      "Plaats mol-werende ultrasoon-pinnen aan tuin-randen",
      "Vul oude gangen direct dicht zodat ze niet hergebruikt worden",
    ],
    visits: "2-3 bezoeken",
    duration: "Resultaat binnen 1-3 weken (afhankelijk van populatie)",
  },
  {
    slug: "houtworm",
    nl: "Houtworm",
    lat: "Anobium punctatum / Hylotrupes bajulus",
    from: "",
    comingSoon: true,
    short: "Gaatjes in oud meubel of dakbalk? Wij behandelen tegen gewone houtworm én huisboktor, voordat de schade structureel wordt.",
    intro: "Houtworm vreet zich jarenlang door balken, vloeren, meubels en kapconstructies, vaak ongezien. Pas wanneer je gaatjes en houtmeel ziet, is de schade al aanzienlijk. Wij identificeren of het gewone houtworm (Anobium) of de veel gevaarlijkere huisboktor (Hylotrupes) is, en behandelen met de juiste methode voor permanente eliminatie.",
    signs: [
      "Ronde gaatjes (1-3 mm) in meubels, balken of vloeren, verse hebben scherpe randen",
      "Houtmeel onder of in de gaatjes, fijn poeder als ovaal hoopje",
      "Knagende geluiden 's avonds of 's nachts in oude balken (huisboktor)",
      "Verzakkingen, kraken of doorbuigen van vloeren / kapconstructie",
      "Grotere gaten (4-10 mm) wijzen op huisboktor, agressiever, vereist directe aanpak",
    ],
    method: [
      "Soortbepaling: gewone houtworm vs huisboktor (totaal andere aanpak en risico)",
      "Injectiebehandeling met permethrine-basis in aangetaste balken/meubels",
      "Oppervlaktebehandeling (penetrerend) op nabijgelegen onbesmette delen",
      "Bij huisboktor in dakconstructie: thermische behandeling of fumigatie",
      "Nacontrole na 6 maanden, bij nieuwe gaatjes vervolgaanpak",
    ],
    prevent: [
      "Houd luchtvochtigheid onder 55%, houtworm gedijt bij vocht",
      "Verlucht zolders en kruipruimtes structureel",
      "Behandel kwetsbaar oud hout preventief met houtbeschermer",
      "Inspecteer jaarlijks oude balken in monumenten of houten panden",
    ],
    visits: "1-2 bezoeken + nacontrole na 6 maanden",
    duration: "Behandeling 1 dag, monitoring 6-12 maanden",
  },
];

// All three provinces — 12 primary cities each (Zuid-Holland, Noord-Holland, Utrecht).
export const STEDEN = [
  // ── Zuid-Holland ─────────────────────────────────────────────
  { slug: "rotterdam",       naam: "Rotterdam",           provincie: "Zuid-Holland",  inwoners: "664.000", primary: true },
  { slug: "den-haag",        naam: "Den Haag",            provincie: "Zuid-Holland",  inwoners: "565.000", primary: true },
  { slug: "leiden",          naam: "Leiden",              provincie: "Zuid-Holland",  inwoners: "127.000", primary: true },
  { slug: "delft",           naam: "Delft",               provincie: "Zuid-Holland",  inwoners: "107.000", primary: true },
  { slug: "dordrecht",       naam: "Dordrecht",           provincie: "Zuid-Holland",  inwoners: "121.000", primary: true },
  { slug: "zoetermeer",      naam: "Zoetermeer",          provincie: "Zuid-Holland",  inwoners: "126.000", primary: true },
  { slug: "schiedam",        naam: "Schiedam",            provincie: "Zuid-Holland",  inwoners: "80.000",  primary: true },
  { slug: "vlaardingen",     naam: "Vlaardingen",         provincie: "Zuid-Holland",  inwoners: "75.000",  primary: true },
  { slug: "capelle",         naam: "Capelle a/d IJssel",  provincie: "Zuid-Holland",  inwoners: "67.000",  primary: true },
  { slug: "spijkenisse",     naam: "Spijkenisse",         provincie: "Zuid-Holland",  inwoners: "73.000",  primary: true },
  { slug: "gouda",           naam: "Gouda",               provincie: "Zuid-Holland",  inwoners: "75.000",  primary: true },
  { slug: "alphen",          naam: "Alphen a/d Rijn",     provincie: "Zuid-Holland",  inwoners: "115.000", primary: true },
  { slug: "westland",        naam: "Westland",            provincie: "Zuid-Holland",  inwoners: "112.000", primary: true },
  { slug: "leidschendam",    naam: "Leidschendam-Voorburg", provincie: "Zuid-Holland", inwoners: "78.000", primary: true },
  { slug: "katwijk",         naam: "Katwijk",             provincie: "Zuid-Holland",  inwoners: "66.000",  primary: true },
  { slug: "hoeksche-waard",  naam: "Hoeksche Waard",      provincie: "Zuid-Holland",  inwoners: "89.000",  primary: true },
  { slug: "lansingerland",   naam: "Lansingerland",       provincie: "Zuid-Holland",  inwoners: "65.000",  primary: true },
  { slug: "rijswijk",        naam: "Rijswijk",            provincie: "Zuid-Holland",  inwoners: "57.000",  primary: true },
  { slug: "pijnacker",       naam: "Pijnacker-Nootdorp",  provincie: "Zuid-Holland",  inwoners: "65.000",  primary: true },
  { slug: "wassenaar",       naam: "Wassenaar",           provincie: "Zuid-Holland",  inwoners: "26.000",  primary: true },
  { slug: "barendrecht",     naam: "Barendrecht",         provincie: "Zuid-Holland",  inwoners: "50.000",  primary: true },
  { slug: "krimpenerwaard",  naam: "Krimpenerwaard",      provincie: "Zuid-Holland",  inwoners: "57.000",  primary: true },
  { slug: "zwijndrecht",     naam: "Zwijndrecht",         provincie: "Zuid-Holland",  inwoners: "44.000",  primary: true },
  { slug: "ridderkerk",      naam: "Ridderkerk",          provincie: "Zuid-Holland",  inwoners: "47.000",  primary: true },
  { slug: "goeree",          naam: "Goeree-Overflakkee",  provincie: "Zuid-Holland",  inwoners: "50.000",  primary: true },
  { slug: "krimpen",         naam: "Krimpen a/d IJssel",  provincie: "Zuid-Holland",  inwoners: "29.000",  primary: true },
  { slug: "maassluis",       naam: "Maassluis",           provincie: "Zuid-Holland",  inwoners: "34.000",  primary: true },
  { slug: "waddinxveen",     naam: "Waddinxveen",         provincie: "Zuid-Holland",  inwoners: "32.000",  primary: true },
  { slug: "bodegraven",      naam: "Bodegraven-Reeuwijk", provincie: "Zuid-Holland",  inwoners: "35.000",  primary: true },
  { slug: "teylingen",       naam: "Teylingen",           provincie: "Zuid-Holland",  inwoners: "38.000",  primary: true },
  { slug: "hellevoetsluis",  naam: "Hellevoetsluis",      provincie: "Zuid-Holland",  inwoners: "40.000",  primary: true },
  { slug: "ambacht",         naam: "Hendrik-Ido-Ambacht", provincie: "Zuid-Holland",  inwoners: "31.000",  primary: true },
  { slug: "papendrecht",     naam: "Papendrecht",         provincie: "Zuid-Holland",  inwoners: "32.000",  primary: true },
  { slug: "nieuwkoop",       naam: "Nieuwkoop",           provincie: "Zuid-Holland",  inwoners: "29.000",  primary: true },
  { slug: "sliedrecht",      naam: "Sliedrecht",          provincie: "Zuid-Holland",  inwoners: "26.000",  primary: true },
  { slug: "midden-delfland", naam: "Midden-Delfland",     provincie: "Zuid-Holland",  inwoners: "20.000",  primary: true },
  { slug: "alblasserdam",    naam: "Alblasserdam",        provincie: "Zuid-Holland",  inwoners: "21.000",  primary: true },
  { slug: "hardinxveld",     naam: "Hardinxveld-Giessendam", provincie: "Zuid-Holland", inwoners: "18.000", primary: true },
  { slug: "brielle",         naam: "Brielle",             provincie: "Zuid-Holland",  inwoners: "17.000",  primary: true },

  // ── Noord-Holland ───────────────────────────────────────────
  { slug: "amsterdam",       naam: "Amsterdam",           provincie: "Noord-Holland", inwoners: "919.000", primary: true },
  { slug: "haarlem",         naam: "Haarlem",             provincie: "Noord-Holland", inwoners: "163.000", primary: true },
  { slug: "zaanstad",        naam: "Zaanstad",            provincie: "Noord-Holland", inwoners: "157.000", primary: true },
  { slug: "haarlemmermeer",  naam: "Haarlemmermeer",      provincie: "Noord-Holland", inwoners: "158.000", primary: true },
  { slug: "alkmaar",         naam: "Alkmaar",             provincie: "Noord-Holland", inwoners: "111.000", primary: true },
  { slug: "amstelveen",      naam: "Amstelveen",          provincie: "Noord-Holland", inwoners: "93.000",  primary: true },
  { slug: "hilversum",       naam: "Hilversum",           provincie: "Noord-Holland", inwoners: "91.000",  primary: true },
  { slug: "purmerend",       naam: "Purmerend",           provincie: "Noord-Holland", inwoners: "82.000",  primary: true },
  { slug: "hoorn",           naam: "Hoorn",               provincie: "Noord-Holland", inwoners: "75.000",  primary: true },
  { slug: "velsen",          naam: "Velsen (IJmuiden)",   provincie: "Noord-Holland", inwoners: "68.000",  primary: true },
  { slug: "heerhugowaard",   naam: "Heerhugowaard",       provincie: "Noord-Holland", inwoners: "57.000",  primary: true },
  { slug: "den-helder",      naam: "Den Helder",          provincie: "Noord-Holland", inwoners: "56.000",  primary: true },
  { slug: "beverwijk",       naam: "Beverwijk",           provincie: "Noord-Holland", inwoners: "42.000",  primary: true },
  { slug: "enkhuizen",       naam: "Enkhuizen",           provincie: "Noord-Holland", inwoners: "18.000",  primary: true },
  { slug: "edam-volendam",   naam: "Edam-Volendam",       provincie: "Noord-Holland", inwoners: "37.000",  primary: true },
  { slug: "oostzaan",        naam: "Oostzaan",            provincie: "Noord-Holland", inwoners: "10.000",  primary: true },
  { slug: "wormerland",      naam: "Wormerland",          provincie: "Noord-Holland", inwoners: "16.000",  primary: true },
  { slug: "castricum",       naam: "Castricum",           provincie: "Noord-Holland", inwoners: "37.000",  primary: true },
  { slug: "heiloo",          naam: "Heiloo",              provincie: "Noord-Holland", inwoners: "24.000",  primary: true },
  { slug: "bergen-nh",       naam: "Bergen (NH)",         provincie: "Noord-Holland", inwoners: "30.000",  primary: true },
  { slug: "uitgeest",        naam: "Uitgeest",            provincie: "Noord-Holland", inwoners: "14.000",  primary: true },
  { slug: "uithoorn",        naam: "Uithoorn",            provincie: "Noord-Holland", inwoners: "30.000",  primary: true },
  { slug: "aalsmeer",        naam: "Aalsmeer",            provincie: "Noord-Holland", inwoners: "32.000",  primary: true },
  { slug: "diemen",          naam: "Diemen",              provincie: "Noord-Holland", inwoners: "31.000",  primary: true },
  { slug: "ouder-amstel",    naam: "Ouder-Amstel",        provincie: "Noord-Holland", inwoners: "14.000",  primary: true },
  { slug: "landsmeer",       naam: "Landsmeer",           provincie: "Noord-Holland", inwoners: "12.000",  primary: true },
  { slug: "waterland",       naam: "Waterland",           provincie: "Noord-Holland", inwoners: "17.000",  primary: true },
  { slug: "koggenland",      naam: "Koggenland",          provincie: "Noord-Holland", inwoners: "23.000",  primary: true },
  { slug: "medemblik",       naam: "Medemblik",           provincie: "Noord-Holland", inwoners: "45.000",  primary: true },
  { slug: "drechterland",    naam: "Drechterland",        provincie: "Noord-Holland", inwoners: "20.000",  primary: true },
  { slug: "stede-broec",     naam: "Stede Broec",         provincie: "Noord-Holland", inwoners: "22.000",  primary: true },
  { slug: "schagen",         naam: "Schagen",             provincie: "Noord-Holland", inwoners: "47.000",  primary: true },
  { slug: "texel",           naam: "Texel",               provincie: "Noord-Holland", inwoners: "14.000",  primary: true },
  { slug: "hollands-kroon",  naam: "Hollands Kroon",      provincie: "Noord-Holland", inwoners: "48.000",  primary: true },
  { slug: "bloemendaal",     naam: "Bloemendaal",         provincie: "Noord-Holland", inwoners: "23.000",  primary: true },

  // ── Utrecht ─────────────────────────────────────────────────
  { slug: "utrecht",         naam: "Utrecht",             provincie: "Utrecht",       inwoners: "367.000", primary: true },
  { slug: "amersfoort",      naam: "Amersfoort",          provincie: "Utrecht",       inwoners: "160.000", primary: true },
  { slug: "veenendaal",      naam: "Veenendaal",          provincie: "Utrecht",       inwoners: "67.000",  primary: true },
  { slug: "nieuwegein",      naam: "Nieuwegein",          provincie: "Utrecht",       inwoners: "65.000",  primary: true },
  { slug: "zeist",           naam: "Zeist",               provincie: "Utrecht",       inwoners: "65.000",  primary: true },
  { slug: "stichtse-vecht",  naam: "Stichtse Vecht",      provincie: "Utrecht",       inwoners: "65.000",  primary: true },
  { slug: "woerden",         naam: "Woerden",             provincie: "Utrecht",       inwoners: "52.000",  primary: true },
  { slug: "houten",          naam: "Houten",              provincie: "Utrecht",       inwoners: "50.000",  primary: true },
  { slug: "soest",           naam: "Soest",               provincie: "Utrecht",       inwoners: "46.000",  primary: true },
  { slug: "de-bilt",         naam: "De Bilt",             provincie: "Utrecht",       inwoners: "43.000",  primary: true },
  { slug: "ijsselstein",     naam: "IJsselstein",         provincie: "Utrecht",       inwoners: "34.000",  primary: true },
  { slug: "wijk-bij-duurstede", naam: "Wijk bij Duurstede", provincie: "Utrecht",     inwoners: "24.000",  primary: true },
  { slug: "baarn",           naam: "Baarn",               provincie: "Utrecht",       inwoners: "25.000",  primary: true },
  { slug: "bunnik",          naam: "Bunnik",              provincie: "Utrecht",       inwoners: "16.000",  primary: true },
  { slug: "bunschoten",      naam: "Bunschoten",          provincie: "Utrecht",       inwoners: "22.000",  primary: true },
  { slug: "rhenen",          naam: "Rhenen",              provincie: "Utrecht",       inwoners: "20.000",  primary: true },
  { slug: "utrechtse-heuvelrug", naam: "Utrechtse Heuvelrug", provincie: "Utrecht",   inwoners: "50.000",  primary: true },
  { slug: "lopik",           naam: "Lopik",               provincie: "Utrecht",       inwoners: "14.000",  primary: true },
  { slug: "oudewater",       naam: "Oudewater",           provincie: "Utrecht",       inwoners: "10.000",  primary: true },
  { slug: "montfoort",       naam: "Montfoort",           provincie: "Utrecht",       inwoners: "14.000",  primary: true },
  { slug: "vijfheerenlanden", naam: "Vijfheerenlanden",   provincie: "Utrecht",       inwoners: "57.000",  primary: true },
  { slug: "de-ronde-venen",  naam: "De Ronde Venen",      provincie: "Utrecht",       inwoners: "45.000",  primary: true },
  { slug: "eemnes",          naam: "Eemnes",              provincie: "Utrecht",       inwoners: "10.000",  primary: true },

  // ── Werkgebied-completering 2026-05-24 ───────────────────────────────
  // 20 ontbrekende gemeenten in ZH/NH/UT om dekking van 97 → 117 te brengen
  // (= volledig werkgebied). Alle hebben >5.000 inwoners en zijn officiële
  // CBS-gemeenten. Hierdoor genereert prerender 20 × 16 = 320 extra HTMLs.

  // Zuid-Holland (11 nieuwe)
  { slug: "albrandswaard",   naam: "Albrandswaard",       provincie: "Zuid-Holland",  inwoners: "25.000",  primary: true },
  { slug: "gorinchem",       naam: "Gorinchem",           provincie: "Zuid-Holland",  inwoners: "39.000",  primary: true },
  { slug: "hillegom",        naam: "Hillegom",            provincie: "Zuid-Holland",  inwoners: "23.000",  primary: true },
  { slug: "kaag-en-braassem", naam: "Kaag en Braassem",   provincie: "Zuid-Holland",  inwoners: "27.000",  primary: true },
  { slug: "leiderdorp",      naam: "Leiderdorp",          provincie: "Zuid-Holland",  inwoners: "28.000",  primary: true },
  { slug: "lisse",           naam: "Lisse",               provincie: "Zuid-Holland",  inwoners: "23.000",  primary: true },
  { slug: "molenlanden",     naam: "Molenlanden",         provincie: "Zuid-Holland",  inwoners: "43.000",  primary: true },
  { slug: "noordwijk",       naam: "Noordwijk",           provincie: "Zuid-Holland",  inwoners: "45.000",  primary: true },
  { slug: "oegstgeest",      naam: "Oegstgeest",          provincie: "Zuid-Holland",  inwoners: "26.000",  primary: true },
  { slug: "voorschoten",     naam: "Voorschoten",         provincie: "Zuid-Holland",  inwoners: "26.000",  primary: true },
  { slug: "zuidplas",        naam: "Zuidplas",            provincie: "Zuid-Holland",  inwoners: "45.000",  primary: true },

  // Noord-Holland (6 nieuwe)
  { slug: "gooise-meren",    naam: "Gooise Meren",        provincie: "Noord-Holland", inwoners: "60.000",  primary: true },
  { slug: "huizen",          naam: "Huizen",              provincie: "Noord-Holland", inwoners: "41.000",  primary: true },
  { slug: "heemskerk",       naam: "Heemskerk",           provincie: "Noord-Holland", inwoners: "39.000",  primary: true },
  { slug: "heemstede",       naam: "Heemstede",           provincie: "Noord-Holland", inwoners: "27.000",  primary: true },
  { slug: "wijdemeren",      naam: "Wijdemeren",          provincie: "Noord-Holland", inwoners: "24.000",  primary: true },
  { slug: "zandvoort",       naam: "Zandvoort",           provincie: "Noord-Holland", inwoners: "18.000",  primary: true },

  // Utrecht (3 nieuwe)
  { slug: "leusden",         naam: "Leusden",             provincie: "Utrecht",       inwoners: "31.000",  primary: true },
  { slug: "woudenberg",      naam: "Woudenberg",          provincie: "Utrecht",       inwoners: "14.000",  primary: true },
  { slug: "renswoude",       naam: "Renswoude",           provincie: "Utrecht",       inwoners: "5.300",   primary: true },
];

// ─── Postcode → werkgebied-stad mapping ────────────────────────────────
// 4-cijfer-precieze pc4-ranges (web-geverifieerd) i.p.v. de oude grove
// 2-cijfer-map die ernstige fouten gaf (bv. Rijswijk 228x → Haarlem).
// Ranges zijn aaneengesloten 1000-4299 (NH/ZH/UT), elk gekoppeld aan de
// served stad waar de postcode bij hoort, of de dichtstbijzijnde.
// Wijzig je iets? Verifieer ALTIJD met: node scripts/verify-postcodes.mjs
export const PC4_RANGES = [
  // 1000-1999 (Noord-Holland)
  [1000,1109,"amsterdam"],[1110,1113,"diemen"],[1114,1116,"ouder-amstel"],[1117,1119,"haarlemmermeer"],
  [1120,1129,"landsmeer"],[1130,1135,"edam-volendam"],[1136,1156,"waterland"],[1157,1175,"haarlemmermeer"],
  [1176,1189,"amstelveen"],[1190,1199,"ouder-amstel"],[1200,1229,"hilversum"],[1230,1244,"wijdemeren"],
  [1245,1299,"huizen"],[1300,1379,"huizen"],[1380,1389,"amsterdam"],[1390,1392,"de-ronde-venen"],
  [1393,1393,"stichtse-vecht"],[1394,1395,"wijdemeren"],[1396,1397,"de-ronde-venen"],[1398,1419,"gooise-meren"],
  [1420,1425,"uithoorn"],[1426,1427,"de-ronde-venen"],[1428,1429,"nieuwkoop"],[1430,1434,"aalsmeer"],
  [1435,1439,"haarlemmermeer"],[1440,1451,"purmerend"],[1452,1455,"waterland"],[1456,1459,"wormerland"],
  [1460,1470,"purmerend"],[1471,1480,"edam-volendam"],[1481,1482,"purmerend"],[1483,1488,"alkmaar"],
  [1489,1499,"castricum"],[1500,1509,"zaanstad"],[1510,1519,"oostzaan"],[1520,1529,"zaanstad"],
  [1530,1535,"wormerland"],[1536,1539,"alkmaar"],[1540,1545,"zaanstad"],[1546,1549,"wormerland"],
  [1550,1599,"zaanstad"],[1600,1605,"enkhuizen"],[1606,1609,"drechterland"],[1610,1615,"stede-broec"],
  [1616,1618,"drechterland"],[1619,1619,"medemblik"],[1620,1629,"hoorn"],[1630,1635,"koggenland"],
  [1636,1639,"alkmaar"],[1640,1645,"koggenland"],[1646,1646,"alkmaar"],[1647,1653,"koggenland"],
  [1654,1688,"medemblik"],[1689,1690,"hoorn"],[1691,1694,"medemblik"],[1695,1695,"hoorn"],
  [1696,1699,"drechterland"],[1700,1709,"heerhugowaard"],[1710,1719,"medemblik"],[1720,1729,"heerhugowaard"],
  [1730,1737,"hollands-kroon"],[1738,1759,"schagen"],[1760,1779,"hollands-kroon"],[1780,1789,"den-helder"],
  [1790,1799,"texel"],[1800,1829,"alkmaar"],[1830,1834,"heerhugowaard"],[1835,1849,"alkmaar"],
  [1850,1859,"heiloo"],[1860,1899,"bergen-nh"],[1900,1909,"castricum"],[1910,1919,"uitgeest"],
  [1920,1929,"castricum"],[1930,1939,"bergen-nh"],[1940,1949,"beverwijk"],[1950,1959,"velsen"],
  [1960,1969,"heemskerk"],[1970,1999,"velsen"],
  // 2000-2999 (NH-zuid + Zuid-Holland)
  [2000,2040,"haarlem"],[2041,2049,"zandvoort"],[2050,2099,"bloemendaal"],[2100,2119,"heemstede"],
  [2120,2129,"bloemendaal"],[2130,2159,"haarlemmermeer"],[2160,2169,"lisse"],[2170,2179,"hillegom"],
  [2180,2189,"teylingen"],[2190,2199,"haarlemmermeer"],[2200,2219,"noordwijk"],[2220,2239,"katwijk"],
  [2240,2249,"wassenaar"],[2250,2259,"voorschoten"],[2260,2279,"leidschendam"],[2280,2289,"rijswijk"],
  [2290,2299,"westland"],[2300,2339,"leiden"],[2340,2349,"oegstgeest"],[2350,2359,"leiderdorp"],
  [2360,2369,"alphen"],[2370,2379,"kaag-en-braassem"],[2380,2389,"leiden"],[2390,2399,"alphen"],
  [2400,2409,"alphen"],[2410,2419,"bodegraven"],[2420,2489,"nieuwkoop"],[2490,2599,"den-haag"],
  [2600,2629,"delft"],[2630,2649,"pijnacker"],[2650,2659,"lansingerland"],[2660,2669,"midden-delfland"],
  [2670,2699,"westland"],[2700,2729,"zoetermeer"],[2730,2739,"alphen"],[2740,2749,"waddinxveen"],
  [2750,2759,"zuidplas"],[2760,2779,"alphen"],[2780,2799,"zoetermeer"],[2800,2819,"gouda"],
  [2820,2839,"krimpenerwaard"],[2840,2849,"zuidplas"],[2850,2899,"krimpenerwaard"],[2900,2929,"capelle"],
  [2930,2949,"krimpen"],[2950,2956,"alblasserdam"],[2957,2979,"molenlanden"],[2980,2989,"ridderkerk"],
  [2990,2994,"barendrecht"],[2995,2999,"zwijndrecht"],
  // 3000-3999 (ZH-zuid + Utrecht)
  [3000,3099,"rotterdam"],[3100,3129,"schiedam"],[3130,3139,"vlaardingen"],[3140,3154,"maassluis"],
  [3155,3159,"midden-delfland"],[3160,3179,"albrandswaard"],[3180,3199,"rotterdam"],[3200,3209,"spijkenisse"],
  [3210,3229,"hellevoetsluis"],[3230,3239,"brielle"],[3240,3259,"goeree"],[3260,3299,"hoeksche-waard"],
  [3300,3329,"dordrecht"],[3330,3339,"zwijndrecht"],[3340,3349,"ambacht"],[3350,3359,"papendrecht"],
  [3360,3369,"sliedrecht"],[3370,3379,"hardinxveld"],[3380,3399,"molenlanden"],[3400,3409,"ijsselstein"],
  [3410,3415,"montfoort"],[3416,3419,"lopik"],[3420,3439,"nieuwegein"],[3440,3449,"woerden"],
  [3450,3459,"utrecht"],[3460,3469,"montfoort"],[3470,3489,"woerden"],[3490,3599,"utrecht"],
  [3600,3639,"stichtse-vecht"],[3640,3659,"de-ronde-venen"],[3660,3669,"wijdemeren"],[3670,3679,"utrechtse-heuvelrug"],
  [3680,3699,"de-ronde-venen"],[3700,3719,"zeist"],[3720,3739,"de-bilt"],[3740,3749,"baarn"],
  [3750,3759,"bunschoten"],[3760,3769,"soest"],[3770,3829,"amersfoort"],[3830,3839,"leusden"],
  [3840,3899,"amersfoort"],[3900,3924,"veenendaal"],[3925,3925,"woudenberg"],[3926,3929,"renswoude"],
  [3930,3939,"woudenberg"],[3940,3959,"utrechtse-heuvelrug"],[3960,3969,"wijk-bij-duurstede"],[3970,3979,"utrechtse-heuvelrug"],
  [3980,3989,"bunnik"],[3990,3999,"houten"],
  // 4000-4299 (Vijfheerenlanden / Gorinchem / Molenlanden + Gelderse/Brabantse grens → dichtstbij)
  [4000,4169,"vijfheerenlanden"],[4170,4219,"gorinchem"],[4220,4229,"molenlanden"],[4230,4239,"vijfheerenlanden"],
  [4240,4242,"molenlanden"],[4243,4249,"vijfheerenlanden"],[4250,4299,"gorinchem"],
];

// Geef de werkgebied-stad-slug voor een (gedeeltelijke) postcode-invoer.
// Retourneert null bij < 4 cijfers; anders ALTIJD een geldige served-slug
// (exacte range, of numeriek dichtstbijzijnde range als fallback).
export function cityForPostcode(input) {
  const digits = String(input ?? '').replace(/\D/g, '');
  if (digits.length < 4) return null;
  const pc4 = parseInt(digits.slice(0, 4), 10);
  for (const [s, e, slug] of PC4_RANGES) if (pc4 >= s && pc4 <= e) return slug;
  let best = null, bestDist = Infinity;
  for (const [s, e, slug] of PC4_RANGES) {
    const d = pc4 < s ? s - pc4 : (pc4 > e ? pc4 - e : 0);
    if (d < bestDist) { bestDist = d; best = slug; }
  }
  return best;
}
